"use client";
import { ArrowDown, ArrowRight } from "@/components/icons/arrow";
import { Image } from "@/components/shared/image";
import { Button, buttonVariants } from "@/components/ui/button";
import { useTranslation } from "@/i18n/client";
import { Lang } from "@/i18n/settings";
import { cn } from "@/lib/utils";
import { NewsProps } from "@/services/news/news.server";
import { MetaProps } from "@/types";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { getNewsClient } from "@/services/news/news.client";
import "dayjs/locale/ar";
import "dayjs/locale/en";
import localeData from "dayjs/plugin/localeData";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import Link from "next/link";
import GradientBorder from "@/components/common/gradient-border";

dayjs.extend(localeData);
dayjs.extend(LocalizedFormat);

type Props = {
  lang: Lang;
  data: NewsProps[];
  meta: MetaProps["pagination"];
};

const MAX_ITEMS = 4;

export function LatestNews({ data: initialData, meta, lang }: Props) {
  const { t } = useTranslation(lang, "news");
  dayjs.locale(lang);

  const [highlight] = useState(initialData?.[0]);
  const [hasMore, setHasMore] = useState((meta?.pageCount ?? 1) > 1);
  const [page, setPage] = useState(1);
  const [data] = useState<NewsProps[]>(initialData?.slice(1, 4));
  const [restData, setRestData] = useState<NewsProps[]>(initialData?.slice(4));
  const [displayedItems, setDisplayedItems] = useState<NewsProps[]>(
    initialData?.slice(4, MAX_ITEMS)
  );
  const [loading, setLoading] = useState(false);
  const [shouldFetchMore, setShouldFetchMore] = useState(false);

  const canLoadMore = hasMore || restData.length > displayedItems.length;

  useEffect(() => {
    const currentDisplayedCount = displayedItems.length;
    const currentRestCount = restData.length;
    if (currentRestCount - currentDisplayedCount < 4 && hasMore) {
      setShouldFetchMore(true);
    }
  }, [displayedItems, restData, hasMore]);

  async function loadMore() {
    if (!canLoadMore || loading) return;

    setLoading(true);

    const currentDisplayedCount = displayedItems.length;
    const currentRestCount = restData.length;

    let newDisplayedItems: NewsProps[] = [];
    let mergeRes: NewsProps[] = [...restData];

    if (
      currentDisplayedCount < MAX_ITEMS ||
      (currentDisplayedCount === currentRestCount && hasMore) ||
      shouldFetchMore
    ) {
      const res = await getNewsClient({
        page: page + 1,
        locale: lang,
        category: "News",
        pageSize: 10,
      });

      if (res?.data) {
        const exIds = mergeRes.map((x) => x.id);
        const fd = res?.data?.filter((x) => !exIds.includes(x.id));

        mergeRes = [...mergeRes, ...fd];

        setRestData(mergeRes);
        setShouldFetchMore(false);
        setHasMore(res.meta.pageCount > page + 1);
        setPage(page + 1);
      }
    }

    const totalItemsToShow = Math.min(
      mergeRes.length || restData.length,
      currentDisplayedCount + MAX_ITEMS
    );

    newDisplayedItems = mergeRes
      .slice(0, totalItemsToShow)
      .slice(currentDisplayedCount, totalItemsToShow);

    setDisplayedItems((prev) => [...prev, ...newDisplayedItems]);

    setLoading(false);
  }

  return (
    <div className="flex flex-col gap-8">
      <h1 className="text-3xl text-secondary md:flex-[0.3] xl:flex-[0.6] xl:text-4xl ltr:font-playfair">
        {t("pageTitle")}
      </h1>

      <div className="flex h-full flex-col gap-4 sm:gap-6 md:gap-8">
        <div className="flex flex-col gap-4 sm:gap-6 md:gap-8 lg:grid lg:grid-cols-2">
          <div className="relative flex max-h-[680px] w-full flex-col rounded-[20px] bg-foreground backdrop-blur-md transition-all duration-200 ease-in hover:bg-secondary/10">
            <GradientBorder radius="20" className="z-10" />
            <div className="flex max-h-[400px] flex-[0.2]">
              <Image
                src={highlight?.image_link}
                alt={highlight?.title ?? ""}
                width={1000}
                height={1000}
                objectFit="cover"
                objectPosition="center"
                className="rounded-t-[20px] object-cover"
              />
            </div>
            <div className="flex h-full flex-1 flex-shrink-0 flex-col gap-4 p-4 sm:gap-6 sm:px-6">
              <div className="flex flex-col gap-4">
                <div className="relative flex items-end gap-1 text-xs text-primary sm:text-base">
                  <p className="">{t("newsTitle")}</p>
                  {highlight?.timeline && (
                    <>
                      <span className="">.</span>
                      <p>{dayjs(new Date(highlight?.timeline)).format("ll")}</p>
                    </>
                  )}
                </div>
                <p className="line-clamp-3 text-lg text-secondary sm:text-xl md:text-2xl md:rtl:text-xl">
                  {highlight?.title}
                </p>
              </div>
              <div>
                <Link
                  href={highlight?.news_link ?? "#"}
                  target="_blank"
                  className={buttonVariants({
                    className:
                      "relative flex !h-12 max-w-fit gap-2 !rounded-[20px] border-none bg-transparent text-sm font-normal text-secondary/90 opacity-80 transition-all duration-150 ease-in hover:text-secondary/90 md:px-6 lg:h-14 xl:text-base ltr:font-sans",
                    variant: "outline",
                  })}
                >
                  <GradientBorder />
                  {highlight?.button_title}
                  <ArrowRight className="-rotate-45 rtl:-rotate-[135deg]" />
                </Link>
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col gap-4 sm:gap-6 md:gap-8 lg:grid lg:grid-rows-3">
            {data?.map((item, i) => (
              <div
                key={i}
                className="flex w-full overflow-hidden rounded-[20px] bg-foreground backdrop-blur-md transition-all duration-200 ease-in hover:bg-secondary/10"
              >
                <GradientBorder className="z-10" radius="20" />
                <div className="flex max-h-[192px] flex-[0.7] flex-shrink-0">
                  <Image
                    src={item?.image_link}
                    alt={item?.title ?? ""}
                    width={500}
                    height={500}
                    className="h-full w-full object-cover ltr:rounded-l-[20px] rtl:rounded-r-[20px]"
                  />
                </div>
                <div className="flex flex-1 flex-shrink-0 flex-col justify-between gap-4 p-4 py-2 sm:gap-6 sm:px-6 sm:py-4">
                  <div className="flex flex-col gap-4">
                    <div className="relative flex items-end gap-2 text-xs text-primary sm:text-base">
                      <p className="">{t("newsTitle")}</p>
                      <span className="">.</span>
                      <p>{dayjs(new Date(item?.timeline)).format("ll")}</p>
                    </div>
                    <p className="line-clamp-2 text-sm text-secondary sm:text-base">
                      {item?.title}
                    </p>
                  </div>
                  <div>
                    <Link
                      href={item?.news_link ?? "#"}
                      target="_blank"
                      className={buttonVariants({
                        className:
                          "relative flex !h-12 gap-2 !rounded-[20px] border-none bg-transparent text-sm font-normal text-secondary/90 opacity-80 transition-all duration-150 ease-in hover:text-secondary/90 xl:text-base ltr:font-sans",
                        variant: "outline",
                      })}
                    >
                      <GradientBorder />

                      {item?.button_title}
                      <ArrowRight className="-rotate-45 rtl:-rotate-[135deg]" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex w-full flex-col gap-4 sm:gap-6 md:grid md:grid-cols-2 md:gap-8">
          {displayedItems?.map((item, i) => (
            <div
              key={i}
              className="back flex w-full overflow-hidden rounded-[20px] bg-foreground backdrop-blur-md transition-all duration-200 ease-in hover:bg-secondary/10"
            >
              <GradientBorder radius="20" className="z-10" />

              <div className="flex max-h-[192px] flex-[0.7] flex-shrink-0">
                <Image
                  src={item?.image_link}
                  alt={item?.title ?? ""}
                  width={500}
                  height={500}
                  className="h-full w-full object-cover ltr:rounded-l-[20px] rtl:rounded-r-[20px]"
                />
              </div>
              <div className="flex flex-1 flex-shrink-0 flex-col justify-between gap-4 p-4 py-2 sm:gap-6 sm:px-6 sm:py-4">
                <div className="flex flex-col gap-4">
                  <div className="relative flex items-end gap-2 text-xs text-primary sm:text-base">
                    <p className="">{t("newsTitle")}</p>
                    <span className="">.</span>
                    <p>{dayjs(new Date(item?.timeline)).format("ll")}</p>
                  </div>
                  <p className="line-clamp-2 text-sm text-secondary sm:text-base">{item?.title}</p>
                </div>
                <div>
                  <Link
                    href={item?.news_link ?? "#"}
                    target="_blank"
                    className={buttonVariants({
                      className:
                        "relative flex !h-12 gap-2 !rounded-[20px] border-none bg-transparent text-sm font-normal text-secondary/90 opacity-80 transition-all duration-150 ease-in hover:text-secondary/90 xl:text-base ltr:font-sans",
                      variant: "outline",
                    })}
                  >
                    <GradientBorder />
                    {item?.button_title}
                    <ArrowRight className="-rotate-45 rtl:-rotate-[135deg]" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
        {loading && (
          <div className="flex w-full flex-col gap-4 sm:gap-6 md:grid md:grid-cols-2 md:gap-8">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex w-full overflow-hidden rounded-[20px] bg-foreground">
                <div className="flex h-[160px] flex-[0.7] flex-shrink-0 lg:h-[182px]">
                  <Skeleton className="w-full object-cover ltr:rounded-l-[20px] rtl:rounded-r-[20px]" />
                </div>
                <div className="flex flex-1 flex-shrink-0 flex-col justify-between gap-4 p-4 py-2 sm:gap-6 sm:px-6 sm:py-4">
                  <div className="flex flex-col gap-4">
                    <div className="relative flex items-end gap-2 text-xs text-primary sm:text-base">
                      <Skeleton className="h-2 w-6" />
                      <Skeleton className="h-2 w-12" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                  </div>
                  <div>
                    <Skeleton className="h-12 w-32 rounded-[20px]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div
          className={cn("mt:pt-0 flex items-center justify-center pt-6", !canLoadMore && "hidden")}
        >
          <Button
            onClick={loadMore}
            disabled={loading || !canLoadMore}
            variant="outline"
            className="flex h-12 max-w-fit gap-2 rounded-full border-none bg-foreground px-8 font-normal uppercase text-secondary/70 opacity-80 backdrop-blur-md transition-all duration-150 ease-in hover:text-secondary/90 lg:h-14 xl:text-base ltr:font-sans"
          >
            <GradientBorder />
            {t("loadMore")}
            <ArrowDown />
          </Button>
        </div>
      </div>
    </div>
  );
}
